import './WidgetStyle.css'; 
import './ResponsiveFonts.css';
import { useFont } from '../../Design/FontContext';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import DeleteModal from './DeleteModal';
import { getTextEffectStyles } from '../utils/textEffectsUtils';
import '../WidgetEditComponents/TextEffectsAnimations.css';
import './MobileBreakpoints.css';
import { generateLayoutStyles, getResponsiveValue, appendPx } from '../WidgetEditComponents/LayoutUtils';

// appendPx wird jetzt aus LayoutUtils importiert

function BragBar({ data, onRemove, isSelected, isEditMode, onUpdate, onSelectWidget }) {
    const deleteLogo = '/img/delete.svg';
    const fonts = useFont();
    
    // INLINE-EDITING SYSTEM
    const [isInlineEditing, setIsInlineEditing] = useState(null);
    const [inlineValue, setInlineValue] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isMobileLayout, setIsMobileLayout] = useState(false);
    
    // Ref für Outside-Click Detection und Mobile-Detection
    const widgetRef = useRef(null);
    const containerRef = useRef(null);

    // Mobile Layout Detection (750px Breakpoint)
    useEffect(() => {
        const checkContainerWidth = () => {
            if (containerRef.current) {
                const containerWidth = containerRef.current.offsetWidth;
                setIsMobileLayout(containerWidth < 750);
            }
        };

        checkContainerWidth();
        window.addEventListener('resize', checkContainerWidth);
        
        const observer = new ResizeObserver(checkContainerWidth);
        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => {
            window.removeEventListener('resize', checkContainerWidth);
            observer.disconnect();
        };
    }, []);

    // Delete Handler
    const handleDelete = (event) => {
        event.stopPropagation();
        if (onRemove) {
            onRemove({ stopPropagation: () => {} }, data.id);
        }
        setShowDeleteModal(false);
    };

    // Helper functions (consistent with other widgets)
    function isDark(color) {
        const rgb = parseInt(color.slice(1), 16);
        const r = (rgb >> 16) & 0xff;
        const g = (rgb >>  8) & 0xff;
        const b = (rgb >>  0) & 0xff;
        const luma = 0.299 * r + 0.587 * g + 0.114 * b;
        return luma < 128;
    }

    function getBorderStyle() {
        if (isEditMode) {
            if (isSelected) {
                const borderColor = isDark(data.backgroundColor) ? '#FFFFFF' : '#000000';
                return `2px dashed ${borderColor}`;
            } else {
                return data.border ? `1.5px solid ${data.borderColor}` : '2px dashed transparent';
            }
        } else {
            return data.border ? `1.5px solid ${data.borderColor}` : 'none';
        }
    }

    // INLINE-EDITING FUNCTIONS
    const handleInlineEdit = (fieldName, currentValue) => {
        if (!isEditMode || !onUpdate) return;
        
        // Widget selektieren wenn nicht bereits selektiert
        if (!isSelected && onSelectWidget) {
            onSelectWidget(data.id);
        }
        
        if (isInlineEditing === fieldName) {
            handleInlineCancel();
            return;
        }
        setIsInlineEditing(fieldName);
        setInlineValue(currentValue || '');
    };

    const handleInlineSubmit = useCallback((fieldName) => {
        if (!onUpdate) {
            console.warn('⚠️ onUpdate not available');
            return;
        }
        
        console.log('💾 BragBar Saving field:', fieldName, 'with value:', inlineValue);
        const updatedData = { ...data, [fieldName]: inlineValue };
        onUpdate(updatedData);
        setIsInlineEditing(null);
        setInlineValue('');
        console.log('✅ BragBar Save completed for:', fieldName);
    }, [onUpdate, data, inlineValue]);

    const handleInlineCancel = () => {
        setIsInlineEditing(null);
        setInlineValue('');
    };

    // Smart Outside-Click Handler
    useEffect(() => {
        if (!isInlineEditing) return;

        const handleOutsideClick = (event) => {
            const isEditingInput = event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA';
            const isActionButton = event.target.hasAttribute('data-action-button') || event.target.closest('[data-action-button]');
            const isDeleteButton = event.target.closest('.delete_button');
            const isWidgetEdit = event.target.closest('.widget-edit-container');
            const isWithinWidget = widgetRef.current?.contains(event.target);
            
            const isClickOnOtherTextElement = event.target.tagName === 'P' || 
                                            event.target.tagName === 'H1' || 
                                            event.target.tagName === 'H2' || 
                                            event.target.tagName === 'H3' || 
                                            event.target.tagName === 'DIV';
            
            const isRealOutsideClick = (!isEditingInput && !isActionButton && !isDeleteButton && !isWidgetEdit && !isWithinWidget) ||
                                      (isClickOnOtherTextElement && isWithinWidget && !isEditingInput);

            if (isRealOutsideClick && isInlineEditing) {
                handleInlineSubmit(isInlineEditing);
            }
        };

        document.addEventListener('mousedown', handleOutsideClick, true);
        return () => document.removeEventListener('mousedown', handleOutsideClick, true);
    }, [isInlineEditing, inlineValue, data, onUpdate, handleInlineSubmit]);

    const handleInlineKeyPress = (e, fieldName) => {
        // Dynamische Detection: Ist es gerade ein Textarea?
        const isCurrentlyTextarea = e.target.tagName === 'TEXTAREA';
        
        if (e.key === 'Enter') {
            if (isCurrentlyTextarea) {
                // Textareas: Enter = neue Zeile, Ctrl+Enter = speichern
                if (e.ctrlKey || e.metaKey) {
                    e.preventDefault();
                    handleInlineSubmit(fieldName);
                }
                // Enter allein = neue Zeile (normales Verhalten)
            } else {
                // Input-Felder: Enter = speichern
                e.preventDefault();
                handleInlineSubmit(fieldName);
            }
        } else if (e.key === 'Escape') {
            handleInlineCancel();
        }
    };

    // Render Editable Text (Enhanced like SimplisticTestimonial)
    const renderEditableText = (fieldName, currentValue, Element = 'h3', style = {}, placeholder = 'Click to edit...') => {
        if (!isEditMode) {
            return currentValue ? (
                <Element style={{
                    ...style,
                    whiteSpace: style.whiteSpace || 'pre-wrap'
                }}>
                    {currentValue}
                </Element>
            ) : null;
        }

        if (isInlineEditing === fieldName) {
            // Action Buttons Container
            const renderActionButtons = () => (
                <div style={{
                    position: 'absolute',
                    top: '-11px',
                    right: '-6px',
                    display: 'flex',
                    gap: '4px',
                    zIndex: 9999,
                    background: 'rgba(255, 255, 255, 0.95)',
                    padding: '2px 4px',
                    borderRadius: '6px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    backdropFilter: 'blur(4px)',
                    border: '1px solid rgba(0, 0, 0, 0.05)'
                }}>
                    <button
                        data-action-button="save"
                        onClick={() => handleInlineSubmit(fieldName)}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{
                            background: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            padding: '0',
                            fontSize: '9px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '16px',
                            height: '16px',
                            transition: 'all 0.2s ease'
                        }}
                        title="Save"
                    >
                        ✓
                    </button>
                    
                    <button
                        data-action-button="cancel"
                        onClick={handleInlineCancel}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            padding: '0',
                            fontSize: '9px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '16px',
                            height: '16px',
                            transition: 'all 0.2s ease'
                        }}
                        title="Cancel"
                    >
                        ×
                    </button>
                </div>
            );

            return (
                <div style={{ position: 'relative', width: '100%' }}>
                    <input
                        type="text"
                        value={inlineValue}
                        onChange={(e) => setInlineValue(e.target.value)}
                        onKeyDown={(e) => handleInlineKeyPress(e, fieldName)}
                        placeholder={placeholder}
                        autoFocus
                        style={{
                            ...style,
                            border: 'none',
                            borderRadius: '0',
                            padding: '0',
                            background: 'transparent',
                            outline: 'none',
                            width: '100%',
                            fontFamily: style.fontFamily,
                            fontSize: style.fontSize,
                            fontWeight: style.fontWeight,
                            color: style.color
                        }}
                    />
                    {renderActionButtons()}
                </div>
            );
        }

        return (
            <Element 
                style={{ 
                    ...style, 
                    cursor: 'pointer',
                    minHeight: '1.2em',
                    border: '2px solid transparent',
                    borderRadius: '4px',
                    padding: '8px',
                    transition: 'all 0.2s ease'
                }}
                onClick={() => handleInlineEdit(fieldName, currentValue)}
                title="Click to edit"
                onMouseEnter={(e) => {
                    if (isEditMode) {
                        e.target.style.backgroundColor = 'rgba(102, 126, 234, 0.05)';
                        e.target.style.borderColor = 'rgba(102, 126, 234, 0.2)';
                    }
                }}
                onMouseLeave={(e) => {
                    if (isEditMode) {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.borderColor = 'transparent';
                    }
                }}
            >
                {currentValue || placeholder}
            </Element>
        );
    };

    // Handle logo click
    const handleLogoClick = (logo) => {
        if (!isEditMode && data.enableLogoLinks && logo.link) {
            window.open(logo.link, data.logoLinkTarget);
        }
    };

    // 🚀 RESPONSIVE LAYOUT STYLES - Mit Revolutionary Layout System
    const layoutStyles = generateLayoutStyles(data, isMobileLayout, {
        backgroundColor: data.backgroundColor,
        border: getBorderStyle(),
        borderRadius: appendPx(data.borderRadius),
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        maxWidth: 'none',
        cursor: isEditMode ? 'pointer' : 'default',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center'
    });

    // Styling (consistent with other widgets) mit Revolutionary Layout System
    const widgetStyle = {
        ...layoutStyles,
        // Fallback für alte Properties (Backwards Compatibility)
        ...(layoutStyles.height === 'auto' && !layoutStyles.minHeight && {
            minHeight: appendPx(data.height || 200)
        }),
        // BragBar-spezifisches Padding (falls nicht über Layout-System gesetzt)
        ...(data.padding && { padding: appendPx(data.padding) })
    };

    const titleStyle = {
        color: data.titleColor,
        fontSize: appendPx(isMobileLayout ? 
            (data.titleSizeMobile || data.titleSize || 24) : 
            (data.titleSize || 24)
        ),
        fontWeight: isMobileLayout ? 
            (data.titleWeightMobile || data.titleWeight || '600') : 
            (data.titleWeight || '600'),
        fontFamily: data.titleFont || fonts.globalSmallFont,
        lineHeight: data.titleLineHeight,
        marginBottom: appendPx(data.titleMarginBottom),
        textAlign: data.titleAlignment,
        letterSpacing: appendPx(data.titleLetterSpacing),
        textTransform: data.titleUppercase ? 'uppercase' : 'none',
        margin: 0,
        ...getTextEffectStyles(data.titleEffect)
    };

    const containerStyle = {
        maxWidth: appendPx(data.contentMaxWidth),
        margin: '0 auto',
        width: '100%'
    };

    const logoContainerStyle = {
        backgroundColor: 'transparent', // Always transparent - no background color
        border: data.logoContainerBorder ? `1px solid ${data.logoContainerBorderColor}` : 'none',
        borderRadius: appendPx(data.logoContainerBorderRadius),
        padding: appendPx(data.logoContainerPadding),
        boxShadow: data.logoContainerShadow ? `0 4px 12px ${data.logoContainerShadowColor}` : 'none',
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: data.logoAlignment,
        alignItems: data.logoVerticalAlignment,
        gap: appendPx(data.logoSpacing)
    };

    const logoStyle = (logo, index) => ({
        height: appendPx(logo.height || data.logoHeight),
        maxWidth: appendPx(logo.width || data.logoMaxWidth),
        width: logo.width ? appendPx(logo.width) : 'auto',
        opacity: data.logoOpacity,
        filter: data.logoFilter,
        transition: data.hoverTransition,
        cursor: (!isEditMode && data.enableLogoLinks && logo.link) ? 'pointer' : 'default',
        objectFit: 'contain'
    });

    // Handle widget click for selection
    const handleWidgetClick = (e) => {
        if (isEditMode && onSelectWidget && !isSelected) {
            e.stopPropagation();
            onSelectWidget(data.id);
        }
    };

    // Responsive styles
    const responsiveStyles = `
        @media (max-width: 1024px) {
            .brag-bar-logo-container {
                gap: ${data.tabletLogoSpacing}px !important;
            }
            
            .brag-bar-logo {
                height: ${data.tabletLogoHeight}px !important;
            }
            
            /* .brag-bar-title {
                font-size: ${data.titleSizeMobile}px !important;
            } DEAKTIVIERT: Jetzt über isMobileLayout gesteuert */
            
            .brag-bar-container {
                padding: ${data.tabletPadding}px !important;
            }
        }
        
        @media (max-width: 768px) {
            .brag-bar-logo-container {
                gap: ${data.mobileLogoSpacing}px !important;
                justify-content: center !important;
            }
            
            .brag-bar-logo {
                height: ${data.mobileLogoHeight}px !important;
            }
            
            .brag-bar-container {
                padding: ${data.mobilePadding}px !important;
            }
        }
        
        /* @media (min-width: 1400px) {
            .brag-bar-title {
                font-size: ${data.titleSizeBig}px !important;
            }
        } DEAKTIVIERT: Jetzt über isMobileLayout gesteuert */
        
        ${data.enableHoverEffects ? `
            .brag-bar-logo:hover {
                opacity: ${data.logoHoverOpacity} !important;
                filter: ${data.logoHoverFilter} !important;
                transform: scale(${data.hoverScale}) !important;
            }
        ` : ''}
        
        ${data.enableAnimations ? `
            .brag-bar-logo {
                opacity: 0;
                transform: translateY(20px);
                animation: ${data.logoAnimationType} ${data.logoAnimationDuration}ms ease forwards;
            }
            
            ${data.logos?.map((_, index) => `
                .brag-bar-logo:nth-child(${index + 1}) {
                    animation-delay: ${index * data.logoAnimationDelay}ms;
                }
            `).join('')}
            
            @keyframes fadeIn {
                to {
                    opacity: ${data.logoOpacity};
                    transform: translateY(0);
                }
            }
            
            @keyframes slideInUp {
                to {
                    opacity: ${data.logoOpacity};
                    transform: translateY(0);
                }
            }
            
            @keyframes scaleIn {
                to {
                    opacity: ${data.logoOpacity};
                    transform: scale(1);
                }
            }
        ` : ''}
        
        ${data.enableInfiniteScroll ? `
            .brag-bar-logo-container {
                animation: infiniteScroll ${data.scrollSpeed}s linear infinite;
            }
            
            ${data.pauseOnHover ? `
                .brag-bar-logo-container:hover {
                    animation-play-state: paused;
                }
            ` : ''}
            
            @keyframes infiniteScroll {
                0% { transform: translateX(0); }
                100% { transform: translateX(-50%); }
            }
        ` : ''}
    `;

    return (
        <>
            <style>{responsiveStyles}</style>
            <div 
                ref={containerRef}
                style={widgetStyle}
                onClick={handleWidgetClick}
                className={`brag-bar ${isSelected ? 'selected' : ''}${isMobileLayout ? ' mobile-layout' : ''}`}
            >
                {/* Delete Button */}
                {isEditMode && (
                    <button className="delete_button" onClick={() => setShowDeleteModal(true)}>
                        <img className="delete_logo" src={deleteLogo} alt="Löschen" />
                    </button>
                )}

                {/* Container */}
                <div style={containerStyle} className="brag-bar-container">
                    {/* Title */}
                    {data.showTitle && renderEditableText('titleText', data.titleText, 'h3', titleStyle, 'Enter brag bar title...')}

                    {/* Logo Container */}
                    <div style={logoContainerStyle} className="brag-bar-logo-container">
                        {data.logos?.map((logo, index) => {
                            const LogoElement = (!isEditMode && data.enableLogoLinks && logo.link) ? 'a' : 'div';
                            const logoProps = LogoElement === 'a' ? {
                                href: logo.link,
                                target: data.logoLinkTarget,
                                rel: 'noopener noreferrer'
                            } : {};

                            return (
                                <LogoElement
                                    key={logo.id}
                                    {...logoProps}
                                    onClick={() => handleLogoClick(logo)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        textDecoration: 'none'
                                    }}
                                >
                                    <img
                                        src={logo.image}
                                        alt={logo.alt}
                                        style={logoStyle(logo, index)}
                                        className="brag-bar-logo"
                                        onError={(e) => {
                                            console.log('Logo failed to load:', logo.image);
                                            e.target.style.display = 'none';
                                        }}
                                        onLoad={() => {
                                            console.log('Logo loaded successfully:', logo.image);
                                        }}
                                    />
                                </LogoElement>
                            );
                        })}
                    </div>
                </div>

                {/* Delete Modal */}
                {isEditMode && showDeleteModal && (
                    <DeleteModal
                        isOpen={showDeleteModal}
                        onClose={() => setShowDeleteModal(false)}
                        onConfirm={handleDelete}
                    />
                )}
            </div>
        </>
    );
}

export default BragBar;
